import React, { useRef, useState, useCallback } from 'react';
import FiltersBar from './FiltersBar';
import MobileStartupCard from './MobileStartupCard';

const MAX_CARDS = 60;
const PEEK = 52; // px of the sheet that stays visible (the handle) when collapsed

const MobileHomeSheet = (props) => {
  const { startups, activeId, onSelect } = props;
  const shown = startups.slice(0, MAX_CARDS);

  const sheetRef = useRef(null);
  const startYRef = useRef(0);
  const [collapsed, setCollapsed] = useState(false);
  const [dragY, setDragY] = useState(null); // live px during a drag; null when idle

  const collapsedPx = useCallback(() => {
    const h = sheetRef.current?.offsetHeight || 0;
    return Math.max(0, h - PEEK);
  }, []);

  const onTouchStart = useCallback((e) => {
    startYRef.current = e.touches[0].clientY;
    setDragY(collapsed ? collapsedPx() : 0);
  }, [collapsed, collapsedPx]);

  const onTouchMove = useCallback((e) => {
    const base = collapsed ? collapsedPx() : 0;
    const next = Math.max(0, Math.min(collapsedPx(), base + (e.touches[0].clientY - startYRef.current)));
    setDragY(next);
  }, [collapsed, collapsedPx]);

  const onTouchEnd = useCallback(() => {
    if (dragY == null) return;
    const cp = collapsedPx();
    const base = collapsed ? cp : 0;
    const moved = Math.abs(dragY - base);
    if (moved < 8) {
      setCollapsed((v) => !v); // treat as a tap -> toggle
    } else {
      setCollapsed(dragY > cp / 2);
    }
    setDragY(null);
  }, [dragY, collapsed, collapsedPx]);

  const transform =
    dragY != null
      ? `translateY(${dragY}px)`
      : collapsed
      ? `translateY(calc(100% - ${PEEK}px))`
      : 'translateY(0)';

  return (
    <div
      ref={sheetRef}
      data-testid="mobile-home-sheet"
      style={{ transform, transition: dragY != null ? 'none' : 'transform 0.3s cubic-bezier(0.22,1,0.36,1)' }}
      className="sm:hidden fixed inset-x-0 bottom-0 z-[820] h-[48vh] bg-white rounded-t-[24px] shadow-[0_-8px_30px_rgba(0,0,0,0.12)] flex flex-col"
    >
      {/* Drag handle (the tab) — swipe down to reveal the map, up or tap to expand */}
      <div
        data-testid="mobile-sheet-handle"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        role="button"
        aria-label={collapsed ? 'Expand list' : 'Collapse to map'}
        style={{ touchAction: 'none' }}
        className="flex justify-center items-center pt-3 pb-2.5 shrink-0 cursor-grab active:cursor-grabbing select-none"
      >
        <div className="h-1.5 w-12 rounded-full bg-slate-300" />
      </div>

      <div className="px-4 pt-1 shrink-0">
        <FiltersBar inline {...props} />
      </div>

      <div className="px-4 pt-3 pb-1 shrink-0 text-[15px] font-bold text-slate-900" data-testid="mobile-startup-count">
        {startups.length} startups
      </div>

      <div className="flex-1 min-h-0">
        <div
          className="h-full flex gap-4 overflow-x-auto snap-x snap-mandatory px-4 pb-4 items-start scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {shown.map((s) => (
            <MobileStartupCard key={s.id} startup={s} active={activeId === s.id} onSelect={onSelect} />
          ))}

          {!startups.length && (
            <div className="w-full text-center text-sm text-slate-500 py-10">No startups match your filters.</div>
          )}

          {startups.length > shown.length && (
            <div className="shrink-0 w-[60vw] snap-center flex items-center justify-center text-center text-[12.5px] text-slate-500 border border-dashed border-slate-300 rounded-2xl px-4 py-6">
              +{startups.length - shown.length} more — refine your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileHomeSheet;
